import { ReactElement, useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Textarea } from 'app/components/ui/textarea'
import { Label } from 'app/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { CATEGORY_OPTIONS } from './featureToggleMetadata'
import { FEATURE_TOGGLE_RELEASES } from './featureToggleReleases'
import type { FeatureRow } from './featureToggleModel'

export type ToggleDefinitionInput = {
  name?: string
  displayName: string
  description?: string | null
  category: string
  release?: string | null
}

// Radix Select reserves the empty string, so "No release" needs a value of its own.
const NO_RELEASE = `none`

export type ToggleDefinitionModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  // When editing, the row whose definition we're editing; null = create mode.
  editing: FeatureRow | null
  // The definition's id (for the update mutation). undefined => create mode.
  editingId?: string
  onSave: (input: ToggleDefinitionInput, id?: string) => Promise<boolean>
  release: string | null
  codeRelease: string | null
}

export function ToggleDefinitionModal({
  open,
  onOpenChange,
  editing,
  editingId,
  onSave,
  release: currentRelease,
  codeRelease,
}: ToggleDefinitionModalProps): ReactElement {
  const [name, setName] = useState(``)
  const [displayName, setDisplayName] = useState(``)
  const [description, setDescription] = useState(``)
  const [category, setCategory] = useState<string>(CATEGORY_OPTIONS[0])
  const [release, setRelease] = useState(NO_RELEASE)
  const [saving, setSaving] = useState(false)

  // Reset the form whenever the modal opens (prefilled in edit mode).
  useEffect(() => {
    if (!open) return
    setName(editing?.name ?? ``)
    setDisplayName(editing?.meta?.label ?? ``)
    setDescription(editing?.meta?.description ?? ``)
    setCategory(editing?.meta?.category ?? CATEGORY_OPTIONS[0])
    setRelease(currentRelease ?? NO_RELEASE)
  }, [open, editing, currentRelease])

  const isEdit = !!editing
  const slugValid = /^[a-z0-9-]+$/.test(name.trim())
  const canSave = displayName.trim().length > 0 && (isEdit || slugValid)
  const releaseOptions = [
    ...Object.entries(FEATURE_TOGGLE_RELEASES).map(([slug, r]) => ({ slug, name: r.name })),
    ...(currentRelease && !(currentRelease in FEATURE_TOGGLE_RELEASES)
      ? [{ slug: currentRelease, name: currentRelease }]
      : []),
  ]

  const handleSave = async () => {
    setSaving(true)
    const chosenRelease = release === NO_RELEASE ? null : release
    const base = {
      displayName: displayName.trim(),
      description: description.trim() || null,
      category,
      ...(chosenRelease !== currentRelease && { release: chosenRelease }),
    }
    // Dispatch (create vs update) is decided by editingId in the store, so the
    // payload must branch on the same key: without a definition id we CREATE
    // (include name); with one we UPDATE (name is immutable, omit it).
    const input = editingId ? base : { name: name.trim(), ...base }
    const ok = await onSave(input, editingId)
    setSaving(false)
    if (ok) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit toggle` : `New toggle`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ft-slug">Slug</Label>
            <Input
              id="ft-slug"
              value={name}
              disabled={isEdit}
              placeholder="my-feature"
              onChange={(e) => setName(e.target.value)}
            />
            {!isEdit && !slugValid && name.length > 0 && (
              <p className="text-xs text-red-500">Lowercase letters, numbers and hyphens only.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ft-name">Display name</Label>
            <Input id="ft-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ft-desc">Description</Label>
            <Textarea id="ft-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ft-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="ft-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ft-release">Release</Label>
            <Select value={release} onValueChange={setRelease}>
              <SelectTrigger id="ft-release">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {!codeRelease && <SelectItem value={NO_RELEASE}>No release</SelectItem>}
                {releaseOptions.map((r) => (
                  <SelectItem key={r.slug} value={r.slug}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {codeRelease && <p className="text-xs text-subtle">Set in code. Choose another release to move it.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="tertiary" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="secondary" size="sm" disabled={!canSave || saving} onClick={handleSave}>
            {isEdit ? `Save` : `Create`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
